import { DEBUG, EDITOR } from 'cc/env';
import { Node, Sprite, log, error, assetManager, SpriteFrame, Label, Component, sp } from 'cc';
import Delay from './Toolkit/Utils/Delay';
import { unwatch, watch } from './Toolkit/Utils/Watch';

function getNodeFullPath(node: Node): string {
    const names: string[] = [];
    let current: Node | null = node;
    while (current) {
        names.unshift(current.name);
        current = current.parent;
    }
    return names.join('/');
}

const originalSpriteOnEnable = Sprite.prototype.onEnable;
Sprite.prototype.onEnable = function onEnable(this: Sprite) {
    // 找出哪個 node 的 spriteFrame 在 onEnable 時已失效（非 null 但已被銷毀)
    if (DEBUG && this.spriteFrame && !this.spriteFrame.isValid) {
        error(
            `[AssetReleaseManager] Sprite.onEnable 時 spriteFrame 已失效: node=${getNodeFullPath(this.node)}, uuid=${this.spriteFrame.uuid}, url=${(this.spriteFrame as unknown as { url?: string }).url ?? ''}`,
        );
    }
    originalSpriteOnEnable.call(this);
};
// #endregion

interface IAssetReleaser {
    collect(rootNode: Node): void;
    tryRelease(): void;
}

abstract class BaseAssetReleaser<T extends Component> implements IAssetReleaser {
    protected readonly uuids: string[] = [];

    public collect(rootNode: Node): void {
        const components = this._getComponents(rootNode);
        components.forEach((comp) => {
            if (this.isAssetValid(comp)) {
                this.uuids.push(this.getAssetUUID(comp));
            }
        });
    }

    public tryRelease() {
        this.uuids.forEach((assetUUID) => {
            const asset = assetManager.assets.get(assetUUID);
            if (asset && asset.refCount <= 0) {
                asset.decRef();
                assetManager.dependUtil.getDeps(assetUUID).forEach((uuid) => {
                    assetManager.assets.get(uuid)?.decRef();
                });
            }
        });
        this.uuids.length = 0;
    }

    private _getComponents(rootNode: Node): T[] {
        return [rootNode.getComponent(this.getComponentType()), ...rootNode.getComponentsInChildren(this.getComponentType())].filter(Boolean);
    }
    protected abstract getComponentType(): new (...args: any[]) => T;
    protected abstract getAssetUUID(component: T): string;
    protected abstract isAssetValid(component: T): boolean;
}

// #region SpriteAssetReleaser
class SpriteAssetReleaser extends BaseAssetReleaser<Sprite> {
    protected getComponentType(): new (...args: any[]) => Sprite {
        return Sprite;
    }
    protected getAssetUUID(component: Sprite): string {
        return component.spriteFrame?._uuid;
    }
    protected isAssetValid(component: Sprite): boolean {
        return !!(component && component.spriteFrame && component.spriteFrame.isValid);
    }
}
// #endregion

// #region LabelAssetReleaser
class LabelAssetReleaser extends BaseAssetReleaser<Label> {
    protected getComponentType(): new (...args: any[]) => Label {
        return Label;
    }
    protected getAssetUUID(component: Label): string {
        return component.font?._uuid;
    }
    protected isAssetValid(component: Label): boolean {
        return !!(component && component.font && component.font.isValid);
    }
}
// #endregion

class SpineAssetReleaser extends BaseAssetReleaser<sp.Skeleton> {
    protected getComponentType(): new (...args: any[]) => sp.Skeleton {
        return sp.Skeleton;
    }
    protected getAssetUUID(component: sp.Skeleton): string {
        return component.skeletonData?._uuid;
    }
    protected isAssetValid(component: sp.Skeleton): boolean {
        return !!(component && component.skeletonData && component.skeletonData.isValid);
    }
}

// #region AssetReleaseManager
export default class AssetReleaseManager {
    private _assetReleasers: IAssetReleaser[] = [new SpriteAssetReleaser(), new LabelAssetReleaser(), new SpineAssetReleaser()];

    public release(destroyRootNode: Node) {
        if (!destroyRootNode || !destroyRootNode.isValid) {
            log('Node is invalid or null, cannot release assets.');
            return;
        }
        this._assetReleasers.forEach((controller) => {
            controller.collect(destroyRootNode);
            // Destroy 時也會做計數，因此等待一幀後再嘗試釋放
            Delay.resolve().then(() => controller.tryRelease());
        });
    }
}
// #endregion

// #region SpriteFrame Count Reference
const onSpriteFrameChanged = function onSpriteFrameChanged(prop: string, newValue: SpriteFrame, oldValue: SpriteFrame) {
    newValue?.addRef();
    oldValue?.decRef(false);
    // unwatch when target is destroying
    if (!this.node) {
        unwatch(this, '_spriteFrame' as keyof Sprite, onSpriteFrameChanged);
    }
};

const originalSpriteOnLoad = Sprite.prototype.onLoad;
Sprite.prototype.onLoad = function onLoad(this: Sprite) {
    originalSpriteOnLoad.call(this);
    if (EDITOR) {
        return;
    }
    this.spriteFrame?.addRef();
    watch(this, '_spriteFrame' as keyof Sprite, onSpriteFrameChanged, 0);
};
// #endregion

const onFontChanged = function onFontChanged(prop: string, newValue: any, oldValue: any) {
    newValue?.addRef();
    oldValue?.decRef(false);
    // unwatch when target is destroying
    if (!this.node) {
        unwatch(this, '_font' as keyof Label, onFontChanged);
    }
};

// #region Font Count Reference
const originalLabelOnLoad = Label.prototype.onLoad;
Label.prototype.onLoad = function onLoad(this: Label) {
    originalLabelOnLoad.call(this);
    if (EDITOR) {
        return;
    }
    // onLoad() 之前可能已經賦值過、卻沒被 watch() 算到的那次 addRef
    this.font?.addRef();
    watch(this, '_font' as keyof Label, onFontChanged, 0);
};

const onSkeletonDataChanged = function onSkeletonDataChanged(prop: string, newValue: any, oldValue: any) {
    newValue?.addRef();
    oldValue?.decRef(false);
    // unwatch when target is destroying
    if (!this.node) {
        unwatch(this, '_skeletonData' as keyof sp.Skeleton, onSkeletonDataChanged);
    }
};

const originalSpineOnLoad = sp.Skeleton.prototype.onLoad;
sp.Skeleton.prototype.onLoad = function onLoad(this: sp.Skeleton) {
    originalSpineOnLoad.call(this);
    if (EDITOR) {
        return;
    }
    // onLoad() 之前可能已經賦值過、卻沒被 watch() 算到的那次 addRef
    this.skeletonData?.addRef();
    watch(this, '_skeletonData' as keyof sp.Skeleton, onSkeletonDataChanged, 0);
};
